import { useCallback } from 'react';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Container from '@ui/container/container';
import WatermarkPctgInput from './controls/watermark-pctg-input';
import WatermarkAxisInput from './controls/watermark-axis-input';
import WatermarkRotateInput from './controls/watermark-rotate-input';
import Checkbox from '@ui/checkbox/checkbox';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import type { IWatermarkElement } from '@/types/watermarks.types';
import WatermarkDurationSelector from './controls/watermark-duration-selector';

type Settings = IWatermarkElement['settings'];

interface AddWatermarkCustomBlockProps {
  settings: Settings;
  onSettingsChange: (settings: Settings) => void;
}

const AddWatermarkCustomBlock = ({ settings, onSettingsChange }: AddWatermarkCustomBlockProps) => {
  const hasText = !!settings.text.value.trim();

  const handleUpdate = useCallback(
    <K extends keyof Settings>(key: K, value: Settings[K]) => {
      onSettingsChange({ ...settings, [key]: value });
    },
    [settings, onSettingsChange]
  );

  return (
    <Container className="flex-column gap-16">
      <Container className="flex-column gap-8">
        <InputWithOverlay
          inputHeight="30px"
          jsxLabel={() => (
            <Text className="L2 padding-l-16 margin-b-2" design="GlobalIcoText3">
              Text
            </Text>
          )}
          inputWidth="100%"
          inputProps={{
            autoComplete: 'off',
            name: 'custom-watermark-text',
            className: 'P2',
            value: settings.text.value,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleUpdate('text', { ...settings.text, value: e.target.value });
            },
          }}
        />
        <Container className="flex gap-8 ai-flex-end">
          <WatermarkPctgInput
            label="Opacity"
            min={0}
            max={100}
            value={String(Math.round((settings.opacity ?? 1) * 100))}
            disabled={!hasText}
            onChange={(val) => {
              const n = parseFloat(val);
              if (!isNaN(n)) handleUpdate('opacity', n / 100);
            }}
          />
          {/* Leaving the following in place in case product decides to add it back */}
          {/* <WatermarkFontSelector
            value={settings.text.font.name}
            onChange={(val) => handleUpdate('text', { ...settings.text, font: { ...settings.text.font, name: val } })}
            disabled={!hasText}
          /> */}
          <WatermarkPctgInput
            label="Scale"
            value={String(Math.round(settings.dimensions.scale * 100))}
            disabled={!hasText}
            onChange={(val) => {
              const n = parseFloat(val);
              if (!isNaN(n)) handleUpdate('dimensions', { ...settings.dimensions, scale: n / 100 });
            }}
          />
        </Container>

        <Container className="flex gap-8">
          <WatermarkAxisInput
            label="Position"
            prefix="X"
            value={isNaN(settings.dimensions.x) ? '' : settings.dimensions.x.toString()}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, x: parseFloat(val) })}
            disabled={!hasText || settings.repeat}
          />
          <WatermarkAxisInput
            prefix="Y"
            value={isNaN(settings.dimensions.y) ? '' : settings.dimensions.y.toString()}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, y: parseFloat(val) })}
            disabled={!hasText || settings.repeat}
          />
          <WatermarkRotateInput
            label="Angle"
            value={String(settings.dimensions.rotation ?? 0)}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, rotation: parseFloat(val) || 0 })}
            disabled={!hasText}
          />
        </Container>

        <Checkbox
          label={
            <Text className="L2" design="GlobalIcoText3">
              Repeat as pattern
            </Text>
          }
          color="var(--global-ico-text-3)"
          className="S2 flex gap-6"
          checked={settings.repeat}
          value="repeat"
          onChange={(_val, isOn) => handleUpdate('repeat', isOn)}
          selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
          unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
          disabled={!hasText}
        />
      </Container>

      <WatermarkDurationSelector
        value={settings.duration}
        onChange={(val) => handleUpdate('duration', val)}
        disabled={!hasText}
      />
    </Container>
  );
};

export default AddWatermarkCustomBlock;
